/**
 * The capability map. Renders the arrays from deriveCapabilityState — it
 * never counts or partitions anything itself.
 *
 * The registry renders SYNCHRONOUSLY and unconditionally: live badges are an
 * enhancement, and the enhancement failing must never take the job down. A
 * hanging fetch therefore shows the full map with pending badges, not a
 * spinner and not an empty state.
 */
import { useState } from 'react';
import WorkspaceOverlay from '../shared/WorkspaceOverlay';
import { filterCapabilityView } from '../../capabilities/deriveCapabilityState';

const BADGE = {
  live:    { cls: 'sol-badge--success', text: 'Live' },
  dormant: { cls: 'sol-badge--warning', text: 'Dormant' },
  unknown: { cls: 'sol-badge--muted',   text: 'Unknown' },
  pending: { cls: 'sol-badge--muted',   text: '—' },
};

function CapabilityRow({ item, onInvoke }) {
  const badge = BADGE[item.state] || BADGE.pending;
  return (
    <button
      type="button"
      className="cap-row"
      onClick={() => onInvoke(item)}
      /* Full MCP description on hover — the visible line is summarized to keep
         rows scannable, so the detail has to remain reachable somewhere. */
      title={item.fullText || undefined}
    >
      <span className="cap-row-main">
        <span className="cap-row-label">{item.label}</span>
        <span className="cap-row-example">{item.reason || item.example}</span>
      </span>
      <span className={`sol-badge ${badge.cls}`}>{badge.text}</span>
    </button>
  );
}

function AttentionRow({ item, onInvoke }) {
  return (
    <button type="button" className="cap-row cap-row--attention" onClick={() => onInvoke(item)}>
      <span className="cap-row-main">
        <span className="cap-row-label">{item.label}</span>
        <span className="cap-row-example">{item.detail}</span>
      </span>
      <span className="sol-badge sol-badge--warning">Fix</span>
    </button>
  );
}

function ServerSection({ section, isOpen, onToggle, onInvoke }) {
  const { counts } = section;
  const tally = [
    counts.read ? `${counts.read} read` : null,
    counts.gated ? `${counts.gated} gated` : null,
    counts.paid ? `${counts.paid} paid` : null,
  ].filter(Boolean).join(' · ');
  return (
    <section className="cap-server">
      <button
        type="button"
        className="cap-server-header"
        aria-expanded={isOpen}
        onClick={() => onToggle(section.serverId)}
      >
        <span className="cap-server-name">{section.serverName}</span>
        <span className="cap-server-count">{section.tools.length} tools</span>
        {tally && <span className="cap-server-tally">{tally}</span>}
        {counts.attention > 0 && <span className="sol-badge sol-badge--warning">Needs attention</span>}
        <span className="cap-server-chevron" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
      </button>
      {/* Rows MOUNT only when open. Collapsing that still renders 81 nodes
          would fix the scrollbar and none of the cost. */}
      {isOpen && (
        <div className="cap-server-body">
          {section.tools.map((item) => (
            <CapabilityRow key={item.id} item={item} onInvoke={onInvoke} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function CapabilitiesPanel({ open, derived, onClose, onInvoke }) {
  const { groups, servers, stateUnavailable } = derived;
  const [query, setQuery] = useState('');
  // Per-render only, deliberately not persisted (§10.2): the map opens in a
  // known state every time, and there is no stale "open" to explain.
  const [openIds, setOpenIds] = useState(() => new Set());
  const toggle = (id) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const view = filterCapabilityView({ groups, servers }, query);

  return (
    <WorkspaceOverlay
      open={open}
      title="Capabilities"
      onClose={onClose}
      className="cap-panel"
      ariaLabel="Capabilities"
    >
      {stateUnavailable && (
        <p className="cap-unavailable">Live state unavailable — showing everything Solomon can do.</p>
      )}
      <input
        type="search"
        className="sol-input cap-filter"
        placeholder="Filter capabilities…"
        aria-label="Filter capabilities"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {view.empty && (
        <p className="sol-empty">Nothing matches “{query}”.</p>
      )}
      <div className="cap-groups">
        {view.groups.map((group) => (
          <section key={group.key} className="cap-group">
            <h3 className="cap-group-title">{group.title}</h3>
            {group.items.map((item) => (
              group.key === 'attention'
                ? <AttentionRow key={item.id} item={item} onInvoke={onInvoke} />
                : <CapabilityRow key={item.id} item={item} onInvoke={onInvoke} />
            ))}
          </section>
        ))}
        {view.servers.length > 0 && (
          // ONE grid item holds every server. A `.cap-server` placed directly in
          // the grid is overflow:hidden, so its automatic minimum height is 0
          // and an over-full grid squashed it to nothing — headers unclickable
          // (browser audit 2026-09-21).
          <section className="cap-group cap-group--servers">
            {view.servers.map((section) => {
              const isOpen = section.forceOpen || openIds.has(section.serverId);
              return (
                <ServerSection
                  key={section.serverId}
                  section={section}
                  isOpen={isOpen}
                  onToggle={toggle}
                  onInvoke={onInvoke}
                />
              );
            })}
          </section>
        )}
      </div>
    </WorkspaceOverlay>
  );
}
